import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShiurOccurrence } from '../../utils/shiurTimes';
import { pickLang } from '../../utils/listingDisplay';
import { Time } from './WeekHours';
import { useIsraelNow } from '../../hooks/useIsraelNow';
import { addDaysToDate } from '../../utils/israelTime';
import { dayOfWeekOf } from '../../utils/openingHours';

/**
 * One shiur time as people read it: "20:30–21:15", "Between Mincha and Maariv
 * · about 17:42", "After Maariv · about 21:40".
 */
export default function ShiurTimeText({ occurrence, showNote = true }: { occurrence: ShiurOccurrence; showNote?: boolean }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const o = occurrence;
  const note = showNote ? pickLang(o.noteEn, o.noteHe, isHebrew) : '';
  const anchorText =
    o.anchor === 'betweenMinchaMaariv'
      ? t('directory.shiur.show.betweenMinchaMaariv')
      : o.anchor
        ? t(`directory.shiur.show.${o.anchor}`, { prayer: t(`prayers.${o.anchorPrayer}`) })
        : '';

  return (
    <span className={showNote ? 'block' : 'inline'}>
      {anchorText ? (
        <>
          {anchorText}
          <span className="text-ash">
            {' · '}
            {t('directory.shiur.show.about', { time: '' })}
            <Time>{o.start}</Time>
          </span>
          {o.end && o.anchor !== 'betweenMinchaMaariv' && (
            <span className="text-ash">
              {' · '}
              {t('directory.shiur.show.until', { time: '' })}
              <Time>{o.end}</Time>
            </span>
          )}
        </>
      ) : (
        <Time>
          {o.start}
          {o.end ? `–${o.end}` : ''}
        </Time>
      )}
      {note && <span className="block text-xs text-ash font-normal">{note}</span>}
    </span>
  );
}

/** "Today · Between Mincha and Maariv · about 18:00", "Sunday · 20:30" — a shiur's next time on cards. */
export function NextShiurText({ next, className = '' }: { next: { date: string; occurrence: ShiurOccurrence } | undefined; className?: string }) {
  const { t } = useTranslation();
  const now = useIsraelNow();
  if (!next) return null;
  const day =
    next.date === now.date
      ? t('directory.shiur.show.today')
      : next.date === addDaysToDate(now.date, 1)
        ? t('directory.shiur.show.tomorrow')
        : t(`directory.dayOfWeek.${dayOfWeekOf(next.date)}`);
  return (
    <span className={`inline-flex flex-wrap items-baseline gap-x-1 text-xs px-2 py-0.5 rounded-[10px] bg-fog text-ink font-medium ${className}`}>
      <span>{day} ·</span>
      <ShiurTimeText occurrence={next.occurrence} showNote={false} />
    </span>
  );
}
